export const MEASURE_UNITS = ['m', 'kg', 'l'];
// ─── The long description, in blocks (ADR-055) ───────────────────────────────
/**
 * What a product page's long description is made of. A theme styles each type
 * its own way and skips a type it does not render.
 */
export const PRODUCT_BLOCK_TYPES = ['text', 'photos', 'highlights', 'list', 'looks', 'categories'];
/**
 * The icons a highlight card can carry — a fixed set, like the colour
 * families: every theme draws each one, so a card never shows a blank.
 */
export const HIGHLIGHT_ICONS = [
    'drape',
    'season',
    'width',
    'weight',
    'composition',
    'care',
    'origin',
    'quality',
    'natural',
    'durable',
];
/** How much each block may hold. The API refuses more; the admin counts against the same numbers. */
export const PRODUCT_BLOCK_LIMITS = {
    blocks: 30,
    photos: 3,
    highlights: 4,
    points: 20,
    heading: 120,
    body: 5000,
    caption: 120,
    label: 60,
    value: 120,
    point: 300,
    shortDescription: 300,
};
// ─── The options library (ADR-052) ───────────────────────────────────────────
/**
 * The colour families a filter offers. A fixed taxonomy — like order statuses,
 * code rather than store data — so "Indigo", "Midnight" and "Sapphire" are one
 * entry, Blue, however a store names its colours.
 */
export const COLOR_FAMILIES = [
    { code: 'black', en: 'Black', ar: 'أسود', hex: '#1f1f1f' },
    { code: 'white', en: 'White', ar: 'أبيض', hex: '#f7f5f0' },
    { code: 'grey', en: 'Grey', ar: 'رمادي', hex: '#8e8e8e' },
    { code: 'beige', en: 'Beige', ar: 'بيج', hex: '#dcc7a1' },
    { code: 'brown', en: 'Brown', ar: 'بني', hex: '#7a5230' },
    { code: 'red', en: 'Red', ar: 'أحمر', hex: '#b3261e' },
    { code: 'pink', en: 'Pink', ar: 'وردي', hex: '#e8a0b4' },
    { code: 'orange', en: 'Orange', ar: 'برتقالي', hex: '#e07b39' },
    { code: 'yellow', en: 'Yellow', ar: 'أصفر', hex: '#e8c547' },
    { code: 'green', en: 'Green', ar: 'أخضر', hex: '#4f7a4a' },
    { code: 'blue', en: 'Blue', ar: 'أزرق', hex: '#3b6ea5' },
    { code: 'navy', en: 'Navy', ar: 'كحلي', hex: '#1f2f4f' },
    { code: 'purple', en: 'Purple', ar: 'بنفسجي', hex: '#6b3f87' },
    { code: 'gold', en: 'Gold', ar: 'ذهبي', hex: '#b8914a' },
    { code: 'silver', en: 'Silver', ar: 'فضي', hex: '#b8bcc2' },
    { code: 'multi', en: 'Multicolour', ar: 'متعدد الألوان', hex: '#a0a0a0' },
];
/**
 * The family a swatch most likely belongs to — a suggestion for an operator to
 * accept or change, never applied on its own: "rose gold" reads as pink to a
 * hue wheel and as gold to a fabric buyer.
 */
export function suggestColorFamily(hex) {
    const match = /^#?([0-9a-f]{6})$/i.exec(hex?.trim() ?? '');
    if (!match)
        return null;
    const value = parseInt(match[1], 16);
    const [r, g, b] = [(value >> 16) & 255, (value >> 8) & 255, value & 255].map((channel) => channel / 255);
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const lightness = (max + min) / 2;
    const delta = max - min;
    const saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));
    let hue = 0;
    if (delta !== 0) {
        if (max === r)
            hue = 60 * (((g - b) / delta) % 6);
        else if (max === g)
            hue = 60 * ((b - r) / delta + 2);
        else
            hue = 60 * ((r - g) / delta + 4);
    }
    if (hue < 0)
        hue += 360;
    if (lightness < 0.16)
        return 'black';
    if (lightness > 0.9 && saturation < 0.35)
        return 'white';
    if (saturation < 0.14)
        return lightness > 0.72 ? 'silver' : 'grey';
    if (hue >= 18 && hue < 55) {
        if (lightness > 0.72)
            return saturation > 0.6 && hue < 30 ? 'pink' : 'beige';
        // A dark yellow-brown is olive to anyone buying cloth.
        if (lightness < 0.42)
            return hue >= 45 ? 'green' : 'brown';
        if (hue >= 32 && saturation < 0.55)
            return 'gold';
        return hue < 32 ? 'orange' : 'gold';
    }
    if (hue < 12 || hue >= 345)
        return lightness > 0.7 ? 'pink' : 'red';
    if (hue < 18)
        return lightness > 0.72 ? 'pink' : 'orange';
    if (hue < 68)
        return 'yellow';
    if (hue < 170)
        return 'green';
    if (hue < 250)
        return lightness < 0.27 ? 'navy' : 'blue';
    if (hue < 300)
        return 'purple';
    return 'pink';
}
//# sourceMappingURL=catalog.js.map